<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Invoice } from '../../../../Types/invoice'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'
import useLoading from '../../../../Composables/useLoading.ts'

type FormCreatorType = InstanceType<typeof FormCreator>

const props = defineProps<{
  invoice: Invoice
}>()

const emit = defineEmits<{
  updated: [{ invoice: Invoice }]
}>()

const { d, n, t } = useI18n()
const { loading, setLoading } = useLoading(true)

const form = ref<FormCreatorType | null>(null)
const updateLoading = ref(false)

async function updateInvoice() {
  updateLoading.value = true
  try {
    await form.value?.methods()
  } finally {
    updateLoading.value = false
  }
}

function submitted(invoice: Invoice) {
  emit('updated', { invoice })
}

function onFormLoad(formCreator: FormCreatorType) {
  form.value = formCreator
}
</script>

<template>
  <div>
    <FormCreator
      :id="invoice.id"
      endpoint="invoices"
      src-error="forminvoice"
      class-to-add="d-none"
      @form-return="submitted"
      @update-data="onFormLoad"
      @is-loading="setLoading"
    />

    <LazyContent :loading="loading" :class="{ 'p-2': loading }">
      <div v-if="form" class="table-responsive">
        <table class="table table-striped table-hover fs-16">
          <tbody>
            <tr>
              <td>{{ t('invoice.label.entity') }}</td>
              <td class="text-right">{{ invoice?.entity?.name || '-' }}</td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.number') }}</td>
              <td class="text-right">{{ invoice.number ?? '-' }}</td>
            </tr>
            <tr>
              <td colspan="2">
                <FieldCreator name="name" form-ref="forminvoice" :form-data="form" domain="invoice" label-top />
              </td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.status') }}</td>
              <td class="text-right">
                {{ invoice.status ? t('invoice.status.' + invoice.status + '.label') : '-' }}
              </td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.invoicedAt') }}</td>
              <td class="text-right">
                {{ invoice.invoicedAt ? d(new Date(invoice.invoicedAt), 'date') : '-' }}
              </td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.validatedAt') }}</td>
              <td class="text-right">
                {{ invoice.validatedAt ? d(new Date(invoice.validatedAt), 'date') : '-' }}
              </td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.amount') }}</td>
              <td class="text-right">{{ invoice.amount ? n(Number(invoice.amount), 'currency') : '-' }}</td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.tax') }}</td>
              <td class="text-right">{{ invoice.tax ? n(Number(invoice.tax), 'currency') : '-' }}</td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.prepayment') }}</td>
              <td class="text-right">
                {{ invoice.prepayment ? n(Number(invoice.prepayment), 'currency') : '-' }}
              </td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.prepaymentClient') }}</td>
              <td class="text-right">
                {{ invoice.prepaymentClient ? n(Number(invoice.prepaymentClient), 'currency') : '-' }}
              </td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.refundClientDepositAmount') }}</td>
              <td class="text-right">
                {{ invoice.refundClientDepositAmount ? n(Number(invoice.refundClientDepositAmount), 'currency') : '-' }}
              </td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.dueAt') }}</td>
              <td class="text-right">{{ invoice.dueAt ? d(new Date(invoice.dueAt), 'date') : '-' }}</td>
            </tr>
            <tr>
              <td colspan="2">
                <FieldCreator name="paymentMean" form-ref="forminvoice" :form-data="form" domain="invoice" label-top />
              </td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.periodAt') }}</td>
              <td class="text-right">{{ invoice.periodAt ?? '-' }}</td>
            </tr>
            <tr>
              <td>{{ t('invoice.label.duration') }}</td>
              <td class="text-right">{{ invoice.duration ?? '-' }}</td>
            </tr>
            <tr>
              <td colspan="2">
                <FieldCreator name="ribCompany" form-ref="forminvoice" :form-data="form" domain="invoice" label-top />
              </td>
            </tr>
            <tr>
              <td colspan="2">
                <FieldCreator name="rib" form-ref="forminvoice" :form-data="form" domain="invoice" label-top />
              </td>
            </tr>
            <tr>
              <td colspan="2">
                <FieldCreator name="comment" form-ref="forminvoice" :form-data="form" domain="invoice" label-top />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-end">
        <ButtonAll
          margin="no"
          type="action"
          icon="modify"
          label="button.update"
          :loading="updateLoading"
          @click="updateInvoice"
        />
      </div>
    </LazyContent>
  </div>
</template>
